import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { setMasterSessionFlag, clearMasterSessionFlag } from "../lib/api";

/**
 * FounderLoginPage — the only login surface. Public route at /founder-login,
 * unlinked from the top nav. Click "Sign in with Google" and Emergent's
 * auth service redirects to Google, then back to /auth-callback with a
 * session_id in the URL fragment.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function FounderLoginPage() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
          { credentials: "include" }
        );
        if (r.ok) {
          const data = await r.json();
          setMe(data);
          // Mirror server-verified master state into local storage so the
          // client-side RequireMaster gate honours the cookie-based session.
          setMasterSessionFlag(!!data?.is_master);
        } else {
          // No valid session — make sure any stale local flag is cleared.
          clearMasterSessionFlag();
        }
      } catch {
        clearMasterSessionFlag();
      }
      setLoading(false);
    })();
  }, []);

  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth-callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
    clearMasterSessionFlag();
    setMe(null);
  };

  return (
    <div className="min-h-screen bg-sage-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md border border-sage-border p-8 space-y-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-faint mb-2">Founder Entry</div>
          <div className="text-3xl text-sage-text tracking-tight">Sign in to Sage</div>
          <div className="text-xs text-sage-muted mt-2 sage-prose">
            Google login. Your email is checked against the founder allow-list on the server. Anyone can sign in; only the founder gets master.
          </div>
        </div>
        {loading ? (
          <div className="text-xs text-sage-faint">Checking session…</div>
        ) : me ? (
          <div className="space-y-4">
            <div className="border border-sage-border p-4 space-y-2">
              <div className="text-xs text-sage-faint">Signed in as</div>
              <div className="text-sm text-sage-text">{me.name || me.email}</div>
              <div className="text-[10px] text-sage-faint tabular-nums">{me.email}</div>
              <div className="text-[10px] uppercase tracking-[0.15em]">
                {me.is_master ? (
                  <span className="text-sage-accent">Master · full access</span>
                ) : (
                  <span className="text-sage-muted">Standard user · no master privileges</span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Link
                to="/sage"
                data-testid="founder-login-goto-chat"
                className="flex-1 text-center border border-sage-accent text-sage-accent px-4 py-2 text-xs uppercase tracking-[0.1em] hover:bg-sage-accent hover:text-sage-bg transition-colors"
              >
                Go to Chat →
              </Link>
              <button
                onClick={logout}
                data-testid="founder-login-logout"
                className="border border-sage-border text-sage-muted px-4 py-2 text-xs uppercase tracking-[0.1em] hover:border-sage-text hover:text-sage-text transition-colors"
              >
                Log out
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={startLogin}
            data-testid="founder-login-google"
            className="w-full border border-sage-accent text-sage-accent px-4 py-3 text-sm uppercase tracking-[0.1em] hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150"
          >
            Sign in with Google →
          </button>
        )}
      </div>
    </div>
  );
}
